// @ts-nocheck
/**
 * VariableInserter — A reusable component that provides:
 * 1. Inline dropdown that auto-appears when user types "{{" in a textarea/input
 * 2. A searchable floating popover with categorized variables
 * 3. A larger modal reference for browsing all available variables
 *
 * Usage:
 *   <VariableInserter
 *     textareaRef={bodyRef}
 *     value={bodyText}
 *     onInsert={(varKey) => insertAtCursor(varKey)}
 *   />
 *
 * Place this component directly after/near the textarea it should monitor.
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  User,
  Building2,
  BarChart3,
  Calendar,
  Mail,
  Phone,
  Hash,
  BookOpen,
  Search,
  X,
  Braces,
  ChevronRight,
  Sparkles,
} from "lucide-react";

// ────────────────────────────────────────────────────
// Complete variable registry with categories
// ────────────────────────────────────────────────────

export interface TemplateVariable {
  key: string;
  label: string;
  category: string;
  source: string;
  example: string;
  description?: string;
}

export const TEMPLATE_VARIABLES: TemplateVariable[] = [
  // Client
  { key: "client_name", label: "Client Full Name", category: "Client", source: "client.first_name + last_name", example: "John Smith", description: "Full name of the client" },
  { key: "first_name", label: "First Name", category: "Client", source: "client.first_name", example: "John", description: "Client's first name" },
  { key: "last_name", label: "Last Name", category: "Client", source: "client.last_name", example: "Smith", description: "Client's last name" },
  { key: "email", label: "Email Address", category: "Client", source: "client.email", example: "john@example.com", description: "Client's primary email" },
  { key: "phone", label: "Phone Number", category: "Client", source: "client.phone", example: "+1 555-0100", description: "Client's phone number" },
  { key: "client_status", label: "Client Status", category: "Client", source: "client.status", example: "Active", description: "Current client status" },
  { key: "date_of_birth", label: "Date of Birth", category: "Client", source: "client.date_of_birth", example: "1985-03-15", description: "Client's date of birth" },
  { key: "address", label: "Address", category: "Client", source: "client.address", example: "123 Main St, NY", description: "Client's mailing address" },

  // Advisor / Firm
  { key: "advisor_name", label: "Advisor Name", category: "Advisor", source: "advisor.full_name", example: "Sarah Johnson", description: "Assigned advisor's full name" },
  { key: "advisor_email", label: "Advisor Email", category: "Advisor", source: "advisor.email", example: "sarah@firm.com", description: "Advisor's email address" },
  { key: "advisor_phone", label: "Advisor Phone", category: "Advisor", source: "advisor.phone", example: "+1 555-0200", description: "Advisor's phone number" },
  { key: "company_name", label: "Company Name", category: "Advisor", source: "tenant.company_name", example: "Vestolio Financial", description: "Firm/company name" },
  { key: "company_website", label: "Company Website", category: "Advisor", source: "tenant.website", example: "www.vestolio.com", description: "Firm website URL" },

  // Assessment Scores
  { key: "score", label: "Overall Score", category: "Assessment", source: "latest_response.overall_score", example: "72", description: "Client's overall financial health score" },
  { key: "planning_score", label: "Planning Score", category: "Assessment", source: "pillar_scores.planning", example: "68", description: "Financial planning pillar score" },
  { key: "investment_score", label: "Investment Score", category: "Assessment", source: "pillar_scores.investment", example: "75", description: "Investment pillar score" },
  { key: "tax_score", label: "Tax Score", category: "Assessment", source: "pillar_scores.tax", example: "61", description: "Tax planning pillar score" },
  { key: "protection_score", label: "Protection Score", category: "Assessment", source: "pillar_scores.protection", example: "80", description: "Risk protection pillar score" },
  { key: "retirement_score", label: "Retirement Score", category: "Assessment", source: "pillar_scores.retirement", example: "55", description: "Retirement readiness score" },
  { key: "last_assessment_date", label: "Last Assessment Date", category: "Assessment", source: "latest_response.created_at", example: "Jan 15, 2024", description: "Date of most recent assessment" },
  { key: "pending_recommendations", label: "Pending Actions", category: "Assessment", source: "actions.pending_count", example: "3", description: "Number of pending action items" },

  // Financial
  { key: "total_assets", label: "Total Assets", category: "Financial", source: "financial_data.total_assets", example: "$1,250,000", description: "Client's total asset value" },
  { key: "total_liabilities", label: "Total Liabilities", category: "Financial", source: "financial_data.total_liabilities", example: "$350,000", description: "Client's total liabilities" },
  { key: "net_worth", label: "Net Worth", category: "Financial", source: "financial_data.net_worth", example: "$900,000", description: "Client's net worth" },
  { key: "annual_income", label: "Annual Income", category: "Financial", source: "financial_data.annual_income", example: "$150,000", description: "Client's annual income" },

  // Dates & Scheduling
  { key: "today", label: "Today's Date", category: "Dates", source: "system.today", example: "June 9, 2026", description: "Current date" },
  { key: "next_appointment", label: "Next Appointment", category: "Dates", source: "appointments.next.date", example: "Jun 15, 2026 at 2:00 PM", description: "Date/time of next scheduled appointment" },
  { key: "last_meeting_date", label: "Last Meeting", category: "Dates", source: "appointments.last.date", example: "May 20, 2026", description: "Date of last meeting" },

  // Household
  { key: "household_name", label: "Household Name", category: "Household", source: "household.name", example: "Smith Household", description: "Client's household name" },
  { key: "household_members", label: "Household Members", category: "Household", source: "household.member_count", example: "4", description: "Number of household members" },

  // Campaign
  { key: "campaign_name", label: "Campaign Name", category: "Campaign", source: "campaign.name", example: "Q1 Outreach", description: "Name of the active campaign" },
  { key: "unsubscribe_link", label: "Unsubscribe Link", category: "Campaign", source: "system.unsubscribe_url", example: "[Unsubscribe]", description: "Link for recipient to unsubscribe" },
];

// Category icons map
const CATEGORY_ICONS: Record<string, any> = {
  Client: User,
  Advisor: Building2,
  Assessment: BarChart3,
  Financial: Hash,
  Dates: Calendar,
  Household: User,
  Campaign: Mail,
};

// Get all unique categories
const CATEGORIES = [...new Set(TEMPLATE_VARIABLES.map((v) => v.category))];

// ────────────────────────────────────────────────────
// Props interface
// ────────────────────────────────────────────────────

interface VariableInserterProps {
  /** Ref to the textarea/input element to monitor for {{ typing */
  textareaRef: React.RefObject<HTMLTextAreaElement | HTMLInputElement>;
  /** Current text value of the textarea (needed to detect {{ trigger) */
  value: string;
  /** Called when a variable is selected. Receives the variable key (without {{ }}) */
  onInsert: (variableKey: string) => void;
  /** Optional: restrict to specific categories */
  categories?: string[];
  /** Optional: additional custom variables */
  customVariables?: TemplateVariable[];
  /** Optional: show the "Insert Variable" button next to the field */
  showButton?: boolean;
  /** Optional: include built-in variables */
  includeBuiltInVariables?: boolean;
}

// ────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────

export default function VariableInserter({
  textareaRef,
  value,
  onInsert,
  categories: filterCategories,
  customVariables = [],
  showButton = true,
  includeBuiltInVariables = true,
}: VariableInserterProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [dropdownSearch, setDropdownSearch] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [triggerStartPos, setTriggerStartPos] = useState<number | null>(null);
  const [triggerLength, setTriggerLength] = useState(2);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const isSelectingRef = useRef(false); // guards against re-entry during dispatchEvent

  // Merge built-in + custom variables
  const allVariables = useMemo(() => {
    const mergedMap = new Map<string, TemplateVariable>();
    if (includeBuiltInVariables) {
      TEMPLATE_VARIABLES.forEach((v) => mergedMap.set(v.key, v));
    }
    customVariables.forEach((v) => mergedMap.set(v.key, v));
    const merged = Array.from(mergedMap.values());
    if (filterCategories?.length) {
      return merged.filter((v) => filterCategories.includes(v.category));
    }
    return merged;
  }, [filterCategories, customVariables, includeBuiltInVariables]);

  // Filter variables based on search
  const filteredVariables = useMemo(() => {
    if (!dropdownSearch) return allVariables;
    const q = dropdownSearch.toLowerCase();
    return allVariables.filter(
      (v) =>
        v.key.includes(q) ||
        v.label.toLowerCase().includes(q) ||
        v.category.toLowerCase().includes(q) ||
        v.description?.toLowerCase().includes(q)
    );
  }, [allVariables, dropdownSearch]);

  // Group variables by category for display
  const groupedVariables = useMemo(() => {
    const groups: Record<string, TemplateVariable[]> = {};
    filteredVariables.forEach((v) => {
      if (!groups[v.category]) groups[v.category] = [];
      groups[v.category].push(v);
    });
    return groups;
  }, [filteredVariables]);

  // ─── Handle variable selection from dropdown ────────────────
  const handleSelectVariable = useCallback(
    (variable: TemplateVariable) => {
      const el = textareaRef?.current;
      if (el && triggerStartPos !== null) {
        // Replace the "{search..." or "{{search..." text with the full variable
        const before = el.value.slice(0, triggerStartPos);
        const cursorPos = el.selectionStart ?? triggerStartPos + triggerLength;
        const after = el.value.slice(cursorPos);
        const insertion = `{{${variable.key}}}`;
        const newValue = before + insertion + after;
        const newCursorPos = triggerStartPos + insertion.length;

        // Close dropdown FIRST to prevent re-entry from the dispatched event
        setShowDropdown(false);
        setTriggerStartPos(null);
        setTriggerLength(2);
        setDropdownSearch("");
        setHighlightedIndex(0);

        // Update via native input event to trigger React's onChange
        isSelectingRef.current = true;
        const nativeInputValueSetter =
          el instanceof HTMLTextAreaElement
            ? Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, "value")?.set
            : Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value")?.set;

        if (nativeInputValueSetter) {
          nativeInputValueSetter.call(el, newValue);
          el.dispatchEvent(new Event("input", { bubbles: true }));
        }
        isSelectingRef.current = false;

        // Restore focus and cursor position
        requestAnimationFrame(() => {
          el.focus();
          el.setSelectionRange(newCursorPos, newCursorPos);
        });
      } else {
        // Fallback: just call onInsert (for button/modal usage)
        onInsert(variable.key);
        setShowDropdown(false);
        setTriggerStartPos(null);
        setTriggerLength(2);
        setDropdownSearch("");
        setHighlightedIndex(0);
      }
    },
    [textareaRef, triggerStartPos, triggerLength, onInsert]
  );

  // ─── Detect "{" or "{{" typing in the textarea ───────────────────
  useEffect(() => {
    const el = textareaRef?.current;
    if (!el) return;

    const detectTrigger = () => {
      // Skip if we're programmatically inserting a variable
      if (isSelectingRef.current) return;

      const cursorPos = el.selectionStart ?? 0;
      const textBefore = (el.value || "").slice(0, cursorPos);

      const matchedTrigger = textBefore.endsWith("{{") ? "{{" : textBefore.endsWith("{") ? "{" : null;

      // Check if the user typed a variable trigger
      if (matchedTrigger) {
        // Calculate position for dropdown
        const rect = el.getBoundingClientRect();
        const lineHeight = parseInt(getComputedStyle(el).lineHeight) || 20;
        const textBeforeCursor = textBefore;
        const lines = textBeforeCursor.split("\n");
        const currentLine = lines.length;
        const scrollTop = el.scrollTop || 0;

        // Approximate cursor position
        const top = rect.top + (currentLine * lineHeight) - scrollTop + lineHeight;
        const charWidth = 8; // approximate monospace char width
        const currentLineText = lines[lines.length - 1] || "";
        const left = rect.left + Math.min(currentLineText.length * charWidth, rect.width - 300);

        setDropdownPosition({
          top: Math.min(top, window.innerHeight - 350),
          left: Math.max(left, rect.left),
        });
        setTriggerStartPos(cursorPos - matchedTrigger.length); // position of the first "{"
        setTriggerLength(matchedTrigger.length);
        setDropdownSearch("");
        setHighlightedIndex(0);
        setShowDropdown(true);
      } else if (showDropdown && triggerStartPos !== null) {
        // User is typing a search query after {{
        const searchText = (el.value || "").slice(triggerStartPos + triggerLength, cursorPos);
        // If user typed "}}" or moved cursor away, close the dropdown
        if (searchText.includes("}}") || cursorPos <= triggerStartPos) {
          setShowDropdown(false);
          setTriggerStartPos(null);
          setTriggerLength(2);
        } else {
          setDropdownSearch(searchText);
          setHighlightedIndex(0);
        }
      }
    };

    const handleInput = () => {
      detectTrigger();
    };

    const handleKeyUp = () => {
      // Some controlled React fields can update value after the native input listener.
      // Read on the next frame as a fallback so typing "{" reliably opens the picker.
      requestAnimationFrame(detectTrigger);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!showDropdown) return;
      if (e.key === "Escape") {
        e.preventDefault();
        setShowDropdown(false);
        setTriggerStartPos(null);
        setTriggerLength(2);
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex((prev) => Math.min(prev + 1, filteredVariables.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === "Enter" || e.key === "Tab") {
        if (filteredVariables.length > 0) {
          e.preventDefault();
          handleSelectVariable(filteredVariables[highlightedIndex] || filteredVariables[0]);
        }
      }
    };

    el.addEventListener("input", handleInput);
    el.addEventListener("keyup", handleKeyUp);
    el.addEventListener("keydown", handleKeyDown);

    return () => {
      el.removeEventListener("input", handleInput);
      el.removeEventListener("keyup", handleKeyUp);
      el.removeEventListener("keydown", handleKeyDown);
    };
  }, [textareaRef, showDropdown, triggerStartPos, triggerLength, filteredVariables, highlightedIndex, handleSelectVariable]);

  // ─── Handle selection from the full modal ────────────────
  const handleModalSelect = useCallback(
    (variable: TemplateVariable) => {
      onInsert(variable.key);
      setShowModal(false);
    },
    [onInsert]
  );

  // ─── Close dropdown on outside click ──────────────────────
  useEffect(() => {
    if (!showDropdown) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        textareaRef.current &&
        !textareaRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
        setTriggerStartPos(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDropdown, textareaRef]);

  return (
    <>
      {/* Trigger button (optional) */}
      {showButton && (
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowModal(true)}
            className="text-xs h-7 gap-1.5 text-indigo-600 border-indigo-200 hover:bg-indigo-50"
          >
            <Braces className="w-3 h-3" />
            Insert Variable
          </Button>
          <span className="text-[11px] text-slate-400">
            or type <kbd className="px-1 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-600 font-mono text-[10px]">{"{"}</kbd> / <kbd className="px-1 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-600 font-mono text-[10px]">{"{{"}</kbd> in the field
          </span>
        </div>
      )}

      {/* Inline floating dropdown (appears on {{ trigger) */}
      {showDropdown && (
        <div
          ref={dropdownRef}
          className="fixed z-[9999] w-[320px] max-h-[300px] rounded-lg border border-slate-200 bg-white shadow-xl overflow-hidden animate-in fade-in-0 zoom-in-95 duration-100"
          style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
        >
          <div className="px-3 py-2 border-b border-slate-100 bg-slate-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <Braces className="w-3.5 h-3.5 text-indigo-500" />
                <span className="text-xs font-semibold text-slate-700">Insert Variable</span>
              </div>
              <button
                type="button"
                className="text-slate-400 hover:text-slate-600"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => { setShowDropdown(false); setTriggerStartPos(null); }}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            {dropdownSearch && (
              <p className="text-[10px] text-slate-500 mt-1">
                Filtering: <span className="font-mono text-indigo-600">{dropdownSearch}</span>
              </p>
            )}
            <p className="text-[10px] text-slate-400 mt-1">↑↓ navigate · Enter to select · Esc to close</p>
          </div>

          <div className="overflow-y-auto max-h-[220px]" id="variable-dropdown-list">
            {filteredVariables.length === 0 ? (
              <div className="px-3 py-4 text-center">
                <p className="text-xs text-slate-500">No matching variables</p>
              </div>
            ) : (
              (() => {
                let flatIndex = -1;
                return Object.entries(groupedVariables).map(([category, vars]) => (
                  <div key={category}>
                    <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-100">
                      <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{category}</span>
                    </div>
                    {vars.map((variable) => {
                      flatIndex++;
                      const isHighlighted = flatIndex === highlightedIndex;
                      return (
                        <button
                          key={variable.key}
                          type="button"
                          data-variable-index={flatIndex}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors border-b border-slate-50 last:border-0 ${
                            isHighlighted ? "bg-indigo-50 border-l-2 border-l-indigo-400" : "hover:bg-slate-50"
                          }`}
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => handleSelectVariable(variable)}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className={`text-xs font-medium truncate ${isHighlighted ? "text-indigo-800" : "text-slate-800"}`}>{variable.label}</span>
                            </div>
                            <span className="text-[10px] text-slate-400 font-mono">{`{{${variable.key}}}`}</span>
                          </div>
                          <span className="text-[10px] text-slate-400 flex-shrink-0 truncate max-w-[80px]">{variable.example}</span>
                        </button>
                      );
                    })}
                  </div>
                ));
              })()
            )}
          </div>

          <div className="px-3 py-2 border-t border-slate-100 bg-slate-50">
            <button
              type="button"
              className="w-full flex items-center justify-center gap-1.5 text-xs text-indigo-600 font-medium hover:text-indigo-800 transition-colors"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => { setShowDropdown(false); setTriggerStartPos(null); setShowModal(true); }}
            >
              <BookOpen className="w-3 h-3" />
              Browse All Variables
              <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      {/* Full variable reference modal */}
      <VariableReferenceModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSelect={handleModalSelect}
        variables={allVariables}
      />
    </>
  );
}

// ────────────────────────────────────────────────────
// Full Variable Reference Modal
// ────────────────────────────────────────────────────

interface VariableReferenceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (variable: TemplateVariable) => void;
  variables: TemplateVariable[];
}

function VariableReferenceModal({ isOpen, onClose, onSelect, variables }: VariableReferenceModalProps) {
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const categories = useMemo(() => [...new Set(variables.map((v) => v.category))], [variables]);

  const filtered = useMemo(() => {
    let result = variables;
    if (selectedCategory) {
      result = result.filter((v) => v.category === selectedCategory);
    }
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (v) =>
          v.key.includes(q) ||
          v.label.toLowerCase().includes(q) ||
          v.category.toLowerCase().includes(q) ||
          v.description?.toLowerCase().includes(q) ||
          v.example.toLowerCase().includes(q)
      );
    }
    return result;
  }, [variables, selectedCategory, search]);

  const grouped = useMemo(() => {
    const groups: Record<string, TemplateVariable[]> = {};
    filtered.forEach((v) => {
      if (!groups[v.category]) groups[v.category] = [];
      groups[v.category].push(v);
    });
    return groups;
  }, [filtered]);

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Braces className="w-5 h-5 text-indigo-600" />
            Dynamic Variables Reference
          </DialogTitle>
          <DialogDescription>
            Click any variable to insert it into your template. Variables are replaced with live data when messages are sent.
          </DialogDescription>
        </DialogHeader>

        {/* Search + Category filter */}
        <div className="flex-shrink-0 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search variables by name, key, or category..."
              className="pl-10"
              autoFocus
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => setSelectedCategory(null)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                !selectedCategory
                  ? "bg-indigo-100 text-indigo-700 border border-indigo-200"
                  : "bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200"
              }`}
            >
              All ({variables.length})
            </button>
            {categories.map((cat) => {
              const Icon = CATEGORY_ICONS[cat] || Hash;
              const count = variables.filter((v) => v.category === cat).length;
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setSelectedCategory(cat === selectedCategory ? null : cat)}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                    selectedCategory === cat
                      ? "bg-indigo-100 text-indigo-700 border border-indigo-200"
                      : "bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200"
                  }`}
                >
                  <Icon className="w-3 h-3" />
                  {cat} ({count})
                </button>
              );
            })}
          </div>
        </div>

        {/* Variable list */}
        <div className="flex-1 overflow-y-auto min-h-0 mt-3 border border-slate-200 rounded-lg">
          {filtered.length === 0 ? (
            <div className="p-8 text-center">
              <Braces className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No variables match your search</p>
            </div>
          ) : (
            Object.entries(grouped).map(([category, vars]) => {
              const Icon = CATEGORY_ICONS[category] || Hash;
              return (
                <div key={category}>
                  <div className="sticky top-0 z-10 px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
                    <Icon className="w-3.5 h-3.5 text-indigo-500" />
                    <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">{category}</span>
                    <Badge variant="outline" className="text-[10px] h-4 px-1.5">{vars.length}</Badge>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {vars.map((variable) => (
                      <button
                        key={variable.key}
                        type="button"
                        onClick={() => onSelect(variable)}
                        className="w-full flex items-center gap-4 px-4 py-3 text-left hover:bg-indigo-50 transition-colors group"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-slate-800 group-hover:text-indigo-700">{variable.label}</span>
                            <code className="text-[11px] font-mono text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">{`{{${variable.key}}}`}</code>
                          </div>
                          {variable.description && (
                            <p className="text-xs text-slate-500 mt-0.5">{variable.description}</p>
                          )}
                        </div>
                        <div className="flex-shrink-0 text-right">
                          <p className="text-[10px] text-slate-400 uppercase tracking-wide">Example</p>
                          <p className="text-xs text-slate-600 font-medium">{variable.example}</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-indigo-400 flex-shrink-0" />
                      </button>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer hint */}
        <div className="flex-shrink-0 pt-3 border-t border-slate-200">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Sparkles className="w-3.5 h-3.5 text-amber-500" />
            <span>
              <strong>Tip:</strong> Type <kbd className="px-1 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-600 font-mono text-[10px]">{"{"}</kbd> or <kbd className="px-1 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-600 font-mono text-[10px]">{"{{"}</kbd> in any template field to quickly search and insert variables.
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────
// Export the modal separately for use as standalone
// ────────────────────────────────────────────────────
export { VariableReferenceModal };
export type { VariableReferenceModalProps };
